/**
 * Types for second-stage refinement actions in Company Discovery
 */

/** Section types that support refinement */
export type RefinementSectionType =
  | "company"
  | "address"
  | "tax"
  | "bankAccounts"
  | "vendors"
  | "customers";

/** Available refinement action types */
export type RefinementActionId =
  // Company Info actions
  | "zefix-lookup"
  | "extract-bank-metadata"
  | "llm-reanalyze"
  // Bank Account actions
  | "validate-ibans"
  | "detect-duplicates"
  | "extract-bank-info"
  // Vendor actions
  | "normalize-vendors"
  | "parse-descriptions"
  | "zefix-validate-vendors"
  | "filter-noise"
  // Customer actions
  | "normalize-customers"
  | "zefix-lookup-customers"
  | "match-to-db";

/** Definition of a refinement action */
export interface RefinementAction {
  id: RefinementActionId;
  label: string;
  description: string;
  icon: string; // Lucide icon name
  /** Sections this action applies to */
  sections: RefinementSectionType[];
  /** Whether action requires user interaction (modal) */
  requiresConfirmation?: boolean;
  /** Whether this action is not yet implemented */
  comingSoon?: boolean;
}

/** All available refinement actions */
export const REFINEMENT_ACTIONS: RefinementAction[] = [
  // Company Info actions
  {
    id: "zefix-lookup",
    label: "Zefix Lookup",
    description: "Search Swiss company registry by name",
    icon: "Search",
    sections: ["company"],
    requiresConfirmation: true,
  },
  {
    id: "extract-bank-metadata",
    label: "Extract from Bank Statements",
    description: "Get account holder info from bank statement headers",
    icon: "FileText",
    sections: ["company"],
  },
  {
    id: "llm-reanalyze",
    label: "Re-analyze Documents",
    description: "Re-process uploaded documents with improved extraction",
    icon: "RefreshCw",
    sections: ["company", "address", "tax"],
  },
  // Bank Account actions
  {
    id: "validate-ibans",
    label: "Validate IBANs",
    description: "Check IBAN format and Swiss bank codes",
    icon: "CheckCircle",
    sections: ["bankAccounts"],
  },
  {
    id: "detect-duplicates",
    label: "Detect Duplicates",
    description: "Find and merge duplicate bank accounts",
    icon: "Copy",
    sections: ["bankAccounts"],
  },
  {
    id: "extract-bank-info",
    label: "Extract Bank Info",
    description: "Get bank name and details from IBAN",
    icon: "Building",
    sections: ["bankAccounts"],
  },
  // Vendor actions
  {
    id: "normalize-vendors",
    label: "Normalize Names",
    description: "Dedupe and clean vendor names",
    icon: "Sparkles",
    sections: ["vendors"],
  },
  {
    id: "parse-descriptions",
    label: "Parse Descriptions",
    description: "Extract actual payee from transaction descriptions",
    icon: "FileSearch",
    sections: ["vendors"],
  },
  {
    id: "zefix-validate-vendors",
    label: "Validate via Zefix",
    description: "Match vendors to registered Swiss companies",
    icon: "BadgeCheck",
    sections: ["vendors"],
  },
  {
    id: "filter-noise",
    label: "Filter Noise",
    description: "Remove banks, addresses, and generic terms",
    icon: "Filter",
    sections: ["vendors"],
  },
  // Customer actions
  {
    id: "normalize-customers",
    label: "Normalize Names",
    description: "Dedupe and clean customer names",
    icon: "Sparkles",
    sections: ["customers"],
  },
  {
    id: "zefix-lookup-customers",
    label: "Zefix Lookup",
    description: "Validate B2B customers via Swiss company registry",
    icon: "BadgeCheck",
    sections: ["customers"],
  },
  {
    id: "match-to-db",
    label: "Match to Database",
    description: "Link to existing customers in your records",
    icon: "Link",
    sections: ["customers"],
  },
];

/** Get actions available for a section */
export function getActionsForSection(
  section: RefinementSectionType
): RefinementAction[] {
  return REFINEMENT_ACTIONS.filter(action => action.sections.includes(section));
}

/** Result of a Zefix company search */
export interface ZefixSearchResult {
  uid: string;
  name: string;
  legalForm: string;
  status: "active" | "inactive" | "deleted";
  address: {
    street?: string;
    houseNumber?: string;
    zip?: string;
    city?: string;
    canton?: string;
  };
  registrationDate?: string;
  similarity: number; // 0-1 score of name match
}

/** Result of a refinement action */
export interface RefinementResult {
  actionId: RefinementActionId;
  success: boolean;
  message: string;
  /** Number of items affected */
  itemsAffected?: number;
  /** Updated data (section-specific) */
  updatedData?: unknown;
  /** Candidates for user selection (e.g., Zefix matches) */
  candidates?: ZefixSearchResult[];
  /** Errors encountered */
  errors?: string[];
}

/** Vendor normalization result */
export interface NormalizedVendor {
  originalName: string;
  normalizedName: string;
  isNoise: boolean;
  confidence: number;
  zefixMatch?: ZefixSearchResult;
  transactionCount: number;
}

/** Customer normalization result */
export interface NormalizedCustomer {
  originalName: string;
  normalizedName: string;
  confidence: number;
  zefixMatch?: ZefixSearchResult;
  existingCustomerId?: number;
  transactionCount: number;
}

/** Bank account validation result */
export interface ValidatedBankAccount {
  iban: string;
  isValid: boolean;
  bankName?: string;
  bankCode?: string;
  isDuplicate?: boolean;
  duplicateOf?: string;
}
